import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, StyledBody } from "baseui/card";
import { Button } from "baseui/button";
import { Input } from "baseui/input";
import { Block } from "baseui/block";
import { Alert } from "baseui/icon";
import Logo from "../../../assets/hmlogo.webp";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasNumber: false,
    hasUppercase: false,
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(
          `${apiUrl}/api/auth/verify-reset-token/${token}`
        );

        setTokenValid(response.data.valid);
        setTokenExpired(response.data.expired || false);
      } catch (error) {
        console.error("Token verification error:", error);
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, apiUrl]);

  useEffect(() => {
    setPasswordStrength({
      hasMinLength: newPassword.length >= 8,
      hasNumber: /\d/.test(newPassword),
      hasUppercase: /[A-Z]/.test(newPassword),
    });
  }, [newPassword]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessageType("error");
      setMessage("Passwords do not match.");
      return;
    }

    if (!passwordStrength.hasMinLength || !passwordStrength.hasNumber || !passwordStrength.hasUppercase) {
      setMessageType("error");
      setMessage("Password does not meet requirements.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${apiUrl}/api/auth/reset-password`, {
        token,
        newPassword,
        confirmPassword,
      });

      if (response.data.success) {
        setMessageType("success");
        setMessage(response.data.message);

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setMessageType("error");
        setMessage(response.data.message || "Failed to reset password.");

        if (response.data.expired) {
          setTokenExpired(true);
        }
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setMessageType("error");

      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
        if (error.response.data.expired) {
          setTokenExpired(true);
        }
      } else {
        setMessage("An error occurred. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <Block
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        backgroundColor="#f7fafc"
      >
        <Card>
          <StyledBody>
            <Block padding="scale800" textAlign="center">
              <p>Verifying reset link...</p>
            </Block>
          </StyledBody>
        </Card>
      </Block>
    );
  }

  if (!tokenValid) {
    return (
      <Block
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Block width={["30%"]}>
          <Card>
            <Block
              display="flex"
              justifyContent="center"
              alignItems="center"
              padding="scale200"
            >
              <img
                src={Logo}
                style={{ backgroundColor: "white" }}
                className="img-fluid"
                alt="Holmes Motors Logo"
              />
            </Block>

            <StyledBody>
              <Block
                display="flex"
                flexDirection="column"
                alignItems="center"
                padding="scale800"
              >
                <Alert size={48} color="#EF4444" />
                <h2 style={{ textAlign: "center", marginTop: "16px" }}>
                  {tokenExpired ? "Link Expired" : "Invalid Reset Link"}
                </h2>
                <p
                  style={{
                    textAlign: "center",
                    color: "#718096",
                    fontSize: "14px",
                    marginBottom: "24px",
                  }}
                >
                  {tokenExpired
                    ? "This password reset link has expired. Please request a new one."
                    : "This password reset link is invalid or has already been used."}
                </p>

                <Button
                  onClick={() => navigate("/admin/forgot-password")}
                  overrides={{
                    BaseButton: { style: { width: "100%" } },
                  }}
                >
                  Request New Reset Link
                </Button>

                <Button
                  kind="tertiary"
                  onClick={() => navigate("/")}
                  overrides={{
                    BaseButton: {
                      style: { width: "100%", marginTop: "12px" },
                    },
                  }}
                >
                  Back to Login
                </Button>
              </Block>
            </StyledBody>
          </Card>
        </Block>
      </Block>
    );
  }

  return (
    <Block
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <Block width={["30%"]}>
        <Card>
          <Block
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="scale200"
          >
            <img
              src={Logo}
              style={{ backgroundColor: "white" }}
              className="img-fluid"
              alt="Holmes Motors Logo"
            />
          </Block>

          <StyledBody>
            <h2 style={{ textAlign: "center" }}>Create New Password</h2>
            <p
              style={{
                textAlign: "center",
                fontSize: "14px",
                marginBottom: "16px",
              }}
            >
              Enter a strong password for your account.
            </p>

            <form onSubmit={handleSubmit}>
              <Block marginBottom="scale600">
                <label htmlFor="newPassword">New Password</label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </Block>

              <Block marginBottom="scale500">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </Block>

              <Block
                marginBottom="scale600"
                padding="scale500"
                backgroundColor="#f7fafc"
                style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
              >
                <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: 500 }}>
                  Password Requirements:
                </p>
                <Block as="ul" style={{ margin: 0, paddingLeft: "20px", fontSize: "13px" }}>
                  <li style={{ color: passwordStrength.hasMinLength ? "#16a34a" : "#64748b" }}>
                    At least 8 characters {passwordStrength.hasMinLength && "✓"}
                  </li>
                  <li style={{ color: passwordStrength.hasNumber ? "#16a34a" : "#64748b" }}>
                    Contains a number {passwordStrength.hasNumber && "✓"}
                  </li>
                  <li style={{ color: passwordStrength.hasUppercase ? "#16a34a" : "#64748b" }}>
                    Contains an uppercase letter {passwordStrength.hasUppercase && "✓"}
                  </li>
                </Block>
              </Block>

              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={
                  isSubmitting ||
                  !newPassword ||
                  !confirmPassword ||
                  !passwordStrength.hasMinLength ||
                  !passwordStrength.hasNumber ||
                  !passwordStrength.hasUppercase
                }
                overrides={{
                  BaseButton: {
                    style: {
                      width: "100%",
                      marginBottom: "16px",
                    },
                  },
                }}
              >
                {isSubmitting ? "Resetting Password..." : "Reset Password"}
              </Button>

              {message && (
                <div
                  style={{
                    color: messageType === "success" ? "green" : "red",
                    textAlign: "center",
                    marginTop: "12px",
                  }}
                >
                  {message}
                </div>
              )}
            </form>

            <Block display="flex" justifyContent="center" marginTop="scale500">
              <Button
                kind="tertiary"
                onClick={() => navigate("/")}
                disabled={isSubmitting}
              >
                Back to Login
              </Button>
            </Block>
          </StyledBody>
        </Card>
      </Block>
    </Block>
  );
}

export default ResetPassword;
